import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { Modal } from "@astrum/ui";
import { GO_CHORDS, SHORTCUTS, isTypingTarget } from "../lib/shortcuts.js";
import "./shortcuts-sheet.css";

/** How long after "g" the second key still counts. */
const CHORD_WINDOW_MS = 1200;

/**
 * Global "?" and "g x" handlers, plus the sheet listing them.
 *
 * Both are ignored while the user is typing, and when a modifier is held, so
 * they never steal a character or a browser shortcut.
 */
export function ShortcutsSheet({ open, onOpen, onClose }: { open: boolean; onOpen: () => void; onClose: () => void }) {
  const navigate = useNavigate();
  const chordAt = useRef(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return;
      if (event.key === "?") {
        event.preventDefault();
        onOpen();
        return;
      }
      if (event.key === "g") {
        chordAt.current = Date.now();
        return;
      }
      const to = GO_CHORDS[event.key];
      if (to && Date.now() - chordAt.current < CHORD_WINDOW_MS) {
        chordAt.current = 0;
        event.preventDefault();
        navigate(to);
        return;
      }
      chordAt.current = 0;
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [navigate, onOpen]);

  const groups = [...new Set(SHORTCUTS.map((s) => s.group))];
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts">
      <div className="shortcuts">
        {groups.map((group) => (
          <section key={group}>
            <h3>{group}</h3>
            <dl>
              {SHORTCUTS.filter((s) => s.group === group).map((s) => (
                <div key={s.keys.join("+") + s.label} className="shortcuts__row">
                  <dt>{s.label}</dt>
                  <dd>
                    {s.keys.map((key, i) => (
                      <span key={i}>
                        {i > 0 && <span className="shortcuts__then">{group === "Go to" ? "then" : "+"}</span>}
                        <kbd>{key}</kbd>
                      </span>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Modal>
  );
}
